import { FunctionComponent, useEffect, useMemo, useState } from 'react'

import { useIsFocused, useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { Icons } from 'atoms'
import { useGetScreensTermsQuery, useUpdateScreenTermsMutation } from 'integration/resources/user'
import { downloadScreenTerm } from 'integration/resources/user/requests'
import { Box, Button, Heading, HStack, Modal, useDisclose, useToast } from 'native-base'
import { RootStackParamList } from 'navigation/types'
import { Toast } from 'organisms'
import {
  downloadFileFromUrl,
  getApiErrorMessage,
  getTermDownloadFileName,
  normalizeTermsDescriptionHtml,
  TERMS_MODAL_CONTENT_MAX_HEIGHT,
  TERMS_MODAL_CONTENT_WIDTH
} from 'utils'

type TermsAcceptanceModalProps = {
  screenName: string
}

export const TermsAcceptanceModal: FunctionComponent<TermsAcceptanceModalProps> = ({
  screenName
}) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()

  const toast = useToast()

  const { onOpen, isOpen, onClose } = useDisclose()

  const { data: screensTerms, isLoading } = useGetScreensTermsQuery()

  const updateTermsMutation = useUpdateScreenTermsMutation()

  const isFocused = useIsFocused()

  const screenTerms = useMemo(() => {
    return screensTerms?.data?.data?.find((screen) => screen.screen_name === screenName)
  }, [screensTerms?.data?.data, screenName])

  const [pendingTerms, setPendingTerms] = useState<
    { id: string; terms_title: string; terms_description: string; is_accepted: boolean }[]
  >([])

  useEffect(() => {
    if (!isFocused) return

    if (!isLoading && screenTerms?.terms) {
      setPendingTerms(screenTerms.terms.filter((term) => !term.is_accepted))
    }
  }, [isFocused, isLoading, screenTerms?.terms, screenTerms?.id])

  const currentTerm = pendingTerms[0]

  const termsContent = currentTerm?.terms_description

  const normalizedTermsContent = useMemo(
    () => (termsContent ? normalizeTermsDescriptionHtml(termsContent) : ''),
    [termsContent]
  )

  const termsTitle = currentTerm?.terms_title ?? 'Termos'

  useEffect(() => {
    if (!isFocused) return

    if (!isLoading && screenTerms) {
      if (!currentTerm && isOpen) {
        onClose()

        return
      }

      if (currentTerm && !isOpen) {
        onOpen()
      }
    }
  }, [currentTerm, isFocused, isLoading, isOpen, onClose, onOpen, screenTerms])

  const handleChooseScreenTerms = (accept: boolean) => {
    onClose()

    if (screenTerms?.id && currentTerm?.id) {
      updateTermsMutation.mutate({
        screen_id: screenTerms.id,
        terms_id: currentTerm.id,
        accept
      })

      if (!accept) navigation.navigate('Home', {})
    }

    setPendingTerms((prevTerms) => prevTerms.filter((term) => term.id !== currentTerm?.id))
  }

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadTerms = async () => {
    if (typeof window === 'undefined' || !currentTerm?.id || isDownloading) return

    setIsDownloading(true)

    try {
      const response = await downloadScreenTerm(currentTerm.id)

      const downloadUrl = response.data.data.download_url

      if (!downloadUrl) {
        throw new Error('Download URL not found')
      }

      const fileName = getTermDownloadFileName(termsTitle, `termos-${screenName}`)

      await downloadFileFromUrl(downloadUrl, fileName)
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error)

      toast.show({
        render: () => <Toast type="error" text={errorMessage} />,
        duration: 6000
      })
    } finally {
      setIsDownloading(false)
    }
  }

  if (!screenTerms) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} bgColor="black">
      <Modal.Content
        w={TERMS_MODAL_CONTENT_WIDTH}
        maxW={TERMS_MODAL_CONTENT_WIDTH}
        borderRadius="20px"
        bgColor="white"
        maxH={TERMS_MODAL_CONTENT_MAX_HEIGHT}
        overflow="hidden"
        display="flex"
        flexDirection="column">
        <Modal.Header pt="64px" w="full" pb={6} bg="white" borderBottomWidth={0} flexShrink={0}>
          <HStack w="full" alignItems="center">
            <Box flex={1}>
              <Heading fontWeight={700} fontSize={24} color="black" textAlign="center">
                {termsTitle}
              </Heading>
            </Box>
          </HStack>

          <Box position="absolute" right={4} top="54px" alignItems="flex-end" zIndex={200}>
            <Button
              variant="ghost"
              colorScheme="gray"
              width="auto"
              onPress={handleDownloadTerms}
              isDisabled={isDownloading || !currentTerm?.id}
              isLoading={isDownloading}
              startIcon={<Icons.Downloading size={4} color="gray.600" />}
              zIndex={200}>
              Download dos termos
            </Button>
          </Box>
        </Modal.Header>
        <Modal.Body w="full" flex={1} minH={0} px={6} py={2} overflowY="auto" alignItems="stretch">
          {normalizedTermsContent ? (
            <div
              className="terms-details-container"
              style={{
                width: '100%',
                maxWidth: '100%'
              }}
              dangerouslySetInnerHTML={{ __html: normalizedTermsContent }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer
          borderTopWidth={0}
          bgColor="white"
          w="full"
          justifyContent="flex-start"
          pt={6}
          pb="64px"
          px={8}
          flexShrink={0}>
          <HStack space={6} w="full">
            <Button
              onPress={() => handleChooseScreenTerms(false)}
              isDisabled={updateTermsMutation.isLoading}
              isLoading={updateTermsMutation.isLoading}
              borderColor="gray.700"
              variant="outline"
              flex={1}
              _text={{
                color: 'gray.700',
                fontSize: '14px',
                lineHeight: '16px'
              }}>
              Recusar
            </Button>
            <Button
              isDisabled={updateTermsMutation.isLoading}
              isLoading={updateTermsMutation.isLoading}
              colorScheme="gray"
              flex={1}
              onPress={() => handleChooseScreenTerms(true)}>
              Aceitar Termos
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
